import type { ApolloQueryResult, QueryOptions } from '@apollo/client';
import isCanceledApiMessage from './isCanceledApiMessage';
import createClient from './apolloClient';
import managerAPI from './managerApi';

/**
 * Runs an Apollo query with cancellation and retry wrapped around it. Attaches an AbortController signal and disables query deduplication unless cancellation is opted out, registers and unregisters that controller in the cancel store, rethrows deliberate cancellations untouched, and otherwise recurses with an incremented attempt count until the retry budget is exhausted.
 *
 * @param options - Apollo query options passed through to the client.
 * @param isUsingCache - Whether the client should serve from cache; defaults to false.
 * @param key - Cancellation store key this request is filed under.
 * @param defaultToken - Auth token to build the client with instead of the session one.
 * @param needCancel - Whether to attach the abort signal and disable deduplication; defaults to true.
 * @param retries - Maximum number of retry attempts; defaults to 1.
 * @param retriesCount - Attempts already made, used by the recursion; defaults to 0.
 * @returns A promise resolving to the Apollo query result.
 */
const apolloQueryWrapper = async <TData, TVariables extends object = object>({
  options,
  isUsingCache = false,
  key = null,
  defaultToken = null,
  needCancel = true,
  retries = 1,
  retriesCount = 0,
}: {
  options: QueryOptions<TVariables, TData>;
  isUsingCache?: boolean;
  key?: string | null;
  defaultToken?: string | null;
  needCancel?: boolean;
  retries?: number;
  retriesCount?: number;
}): Promise<ApolloQueryResult<TData>> => {
  const abortController = new AbortController();
  const contextCancel = {
    fetchOptions: {
      signal: abortController.signal,
    },
    queryDeduplication: false, // Resolve issue: the request isn’t cancelled and subsequent calls of the
    // same query with the same variables don’t work.
  };
  try {
    const apolloClient = await createClient(isUsingCache, defaultToken);
    const promiseQuery = apolloClient.query({
      ...options,
      context: needCancel ? { ...contextCancel } : {},
    });

    // Save to store
    managerAPI.pushStoreCancelAPI(key, abortController);

    const result = await promiseQuery;

    // After API done, remove from store
    managerAPI.removeStoreCancelAPI(key, abortController);

    return result;
  } catch (error) {
    managerAPI.removeStoreCancelAPI(key, abortController);
    if (isCanceledApiMessage(error.message)) {
      throw error;
    } else {
      const updatedCount = retriesCount + 1;
      if (updatedCount > retries) {
        throw error;
      } else {
        return apolloQueryWrapper({
          options,
          isUsingCache,
          key,
          defaultToken,
          needCancel,
          retries,
          retriesCount: updatedCount,
        });
      }
    }
  }
};
export default apolloQueryWrapper;
